import { RouteString } from '@lyra/app/core/enums/route-string';

/**
 * Per-route configuration for {@link QuestionnaireComponent}.
 *
 * Supplied through route `data` and bound to the component's `config` input via
 * `withComponentInputBinding()`. Extend this with extra controls as quiz variants diverge.
 */
/**
 * Questionnaire variant marker. All mini-flow variants switch the page into the vmain mini-flow:
 * minimal header (logo only, centered), no offer banner, and a survey-passed redirect at the end
 * instead of the analyzing/plan screens. `'ali-flow-a'` / `'ali-flow-b'` behave like `'short-quiz'`
 * but land on their own variant's survey-passed page.
 */
export type TQuestionnaireType = 'short-quiz' | 'ali-flow-a' | 'ali-flow-b';

/**
 * Mini-flow variants → the home segment their survey-passed page lives under. A `type` present here
 * runs the mini-flow (skip analyzing, redirect to `[home, survey-passed]` at the end); absent = the
 * full questionnaire (analyzing → plan).
 */
export const MINI_FLOW_HOME: Partial<Record<TQuestionnaireType, RouteString>> = {
  'short-quiz': RouteString.VMAIN,
  'ali-flow-a': RouteString.ALI_PAGE_VARIANT_A,
  'ali-flow-b': RouteString.ALI_PAGE_VARIANT_B,
};

/** Whether `type` runs the vmain mini-flow (short-quiz or an ali flow). */
export const isMiniFlowType = (type: TQuestionnaireType | undefined): boolean => !!type && type in MINI_FLOW_HOME;

export interface IQuestionnaireConfig {
  /** Route the header logo links back to. */
  homePath: RouteString;
  /** Route the offer banner navigates to (keeps the variant's flow self-contained). */
  assessmentPath: RouteString;
  /** Quiz variant; `'short-quiz'` enables the vmain mini-flow behaviour. Omitted = full questionnaire. */
  type?: TQuestionnaireType;
  /** When `true`, hides the limited-offer banner (`mark-cdr-offer`). Defaults to `false`. */
  hideLimitedOffer?: boolean;
}

/** Defaults for the canonical questionnaire route; also the component input fallback. */
export const DEFAULT_QUESTIONNAIRE_CONFIG: IQuestionnaireConfig = {
  homePath: RouteString.MAIN,
  assessmentPath: RouteString.QUESTIONNAIRE,
};
